import { ChangeEvent, useEffect, useState } from 'react';
import { Box, FormGroup, Grid, Typography } from '@mui/material';
import { FormControlLabel } from '@components/formControlLabel';
import { Checkbox } from '@components/checkbox/Checkbox';
import { TextField } from '@components/textField/TextField';
import { BindCard } from '@components/bindCard/BindCard';

const initialState = {
  electronicApproval: false,
  fraudStatements: false,
};

export enum DeclarationType {
  ElectronicApproval = 'electronicApproval',
  FraudStatements = 'fraudStatements',
}

export const electronicApprovalDeclaration = 'I agree to electronic approval';
export const fraudStatementDeclaration = 'I have read the Fraud Statements above';

export type DeclarationCardProps = {
  disabled: boolean;
  statement: string;
  onFieldsChange: (declarationStatus: { areDeclarationFieldsFilled: boolean; declarationName: string }) => void;
};

export const DeclarationCard = (props: DeclarationCardProps) => {
  const { statement, onFieldsChange, disabled } = props;
  const [declarationField, setDeclarationField] = useState(initialState);
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    const areDeclarationFieldsFilled = !!fullName && Object.values(declarationField).every((df) => df);
    onFieldsChange({
      areDeclarationFieldsFilled,
      declarationName: fullName,
    });
    // eslint-disable-next-line
  }, [fullName, declarationField]);

  const handleDeclarationChange = (value: DeclarationType) => (event: ChangeEvent<HTMLInputElement>) => {
    setDeclarationField({
      ...declarationField,
      [value]: event.target.checked,
    });
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
  };

  return (
    <Box id='review-declaration-card' data-testid='review-declaration-card'>
      <BindCard>
        {[
          {
            title: 'Declaration',
            content: (
              <>
                <Typography
                  variant='body'
                  sx={{
                    display: 'block',
                    paddingBottom: '8px',
                    color: 'text.main',
                  }}
                >
                  I DECLARE THAT THE STATEMENTS MADE IN THIS APPLICATION ARE COMPLETE AND TRUE
                </Typography>
                <Typography
                  variant='body'
                  sx={{
                    whiteSpace: 'pre-line',
                    display: 'block',
                    paddingBottom: '18px',
                    color: 'text.main',
                  }}
                >
                  {statement}
                </Typography>
                <Grid size={{ xs: 12, lg: 5 }}>
                  <FormGroup>
                    <FormControlLabel
                      label={electronicApprovalDeclaration}
                      value={DeclarationType.ElectronicApproval}
                      checked={declarationField.electronicApproval}
                      control={
                        <Checkbox
                          data-testid='Electronic Approval'
                          disabled={disabled}
                          value={DeclarationType.ElectronicApproval}
                          checked={declarationField.electronicApproval}
                          onChange={handleDeclarationChange(DeclarationType.ElectronicApproval)}
                        />
                      }
                    />
                    <FormControlLabel
                      label={fraudStatementDeclaration}
                      value={DeclarationType.FraudStatements}
                      checked={declarationField.fraudStatements}
                      control={
                        <Checkbox
                          disabled={disabled}
                          data-testid='Fraud Statements'
                          value={DeclarationType.FraudStatements}
                          checked={declarationField.fraudStatements}
                          onChange={handleDeclarationChange(DeclarationType.FraudStatements)}
                        />
                      }
                    />
                  </FormGroup>
                  <TextField
                    disabled={disabled}
                    data-testid='Electronic Signature'
                    placeholder='Please type your full name'
                    value={fullName}
                    onChangeInput={handleFullNameChange}
                    sx={{ marginTop: '20px' }}
                    fullWidth
                  />
                </Grid>
              </>
            ),
          },
        ]}
      </BindCard>
    </Box>
  );
};
